import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import NamedBox from '../atoms/NamedBox';
import { COLOR_BACKGROUND_WARM, COLOR_BORDER, COLOR_MIDDLE_GRAY, COLOR_TEXT, COLOR_WHITE } from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import { NAIAProductionRow } from '../types/CollegeProductionData';


interface NAIAProductionTableProps {
  className?: string;
  data?: NAIAProductionRow[] | null;
}

const useStyles = makeStyles((theme: Theme) => {
  const firstColWidth = 70;
  const secondColWidth = 170;

  return {
    root: {},
    headerName: {
      fontFamily: FONT_PROXIMA_NOVA,
    },
    tableContainer: {
      maxHeight: 360,
      overflow: 'auto',
    },
    table: {
      width: '100%',
      minWidth: 960,
      borderCollapse: 'collapse' as const,
      tableLayout: 'fixed' as const,
      fontFamily: FONT_PROXIMA_NOVA,
      fontSize: theme.typography.pxToRem(12),
    },
    theadCell: {
      borderBottom: `1px solid ${COLOR_BORDER}`,
      borderRight: `1px solid ${COLOR_BORDER}`,
      padding: theme.spacing(1),
      // background: COLOR_BACKGROUND_WARM,
      color: COLOR_TEXT,
      fontWeight: 700,
      textAlign: 'center' as const,
      // zIndex: 2,
    },
    theadGroupCell: {
      borderBottom: `1px solid ${COLOR_BORDER}`,
      borderRight: `1px solid ${COLOR_BORDER}`,
      padding: theme.spacing(1),
      background: COLOR_MIDDLE_GRAY,
      color: COLOR_TEXT,
      fontWeight: 700,
      textAlign: 'center' as const,
      // zIndex: 2,
    },
    stickyHeaderTop: {
      position: 'sticky' as const,
      top: 0,
    },
    stickyHeaderSecond: {
      position: 'sticky' as const,
      top: 32,
    },
    thFirstCol: {
      minWidth: firstColWidth,
      maxWidth: firstColWidth,
      left: 0,
      // zIndex: 3,
    },
    thSecondCol: {
      minWidth: secondColWidth,
      maxWidth: secondColWidth,
      left: firstColWidth,
      // zIndex: 3,
    },
    stickyCol: {
      position: 'sticky' as const,
      // background: COLOR_BACKGROUND_WARM,
    },
    bodyRow: {
      background: COLOR_WHITE,
    },
    bodyCell: {
      borderBottom: `1px solid ${COLOR_MIDDLE_GRAY}`,
      borderRight: `1px solid ${COLOR_BORDER}`,
      padding: theme.spacing(0.75, 1),
      textAlign: 'center' as const,
      whiteSpace: 'nowrap' as const,
    },
    bodyCellFirst: {
      position: 'sticky' as const,
      left: 0,
      minWidth: firstColWidth,
      maxWidth: firstColWidth,
      background: COLOR_WHITE,
      // zIndex: 2,
      textAlign: 'left' as const,
    },
    bodyCellSecond: {
      position: 'sticky' as const,
      left: firstColWidth,
      minWidth: secondColWidth,
      maxWidth: secondColWidth,
      background: COLOR_WHITE,
      // zIndex: 2,
      textAlign: 'left' as const,
    },
    yearCell: {
      fontWeight: 700,
    },
    groupHeaderRow: {
      borderBottom: `1px solid ${COLOR_BORDER}`,
    },
    groupHeaderCell: {
      background: COLOR_BACKGROUND_WARM,
      borderRight: `1px solid ${COLOR_BORDER}`,
      padding: theme.spacing(1),
      fontWeight: 700,
      textAlign: 'center' as const,
    },
  };
}, { name: 'NAIAProductionTable' });

export default function NAIAProductionTable(props: NAIAProductionTableProps) {
  const { className, data } = props;
  const classes = useStyles();

  // const MOCK_NAIA_PRODUCTION: NAIAProductionResponse = {
  //   idType: 'tfId',
  //   idValue: 10000,
  //   productionData: [
  //     {
  //       year: 2025,
  //       teamName: '',
  //       gamesPlayed: 0,
  //       passingComp: 0,
  //       passingAtt: 0,
  //       passingYds: 0,
  //       passingTD: 0,
  //       passingInt: 0,
  //       rushingAtt: 0,
  //       rushingYds: 0,
  //       rushingTD: 0,
  //       receiving: 0,
  //       receivingYds: 0,
  //       receivingTD: 0,
  //       tackles: 29,
  //       sacks: 0,
  //       interceptions: 3,
  //     },
  //   ],
  // };

  // const rows: NAIAProductionRow[] = (data ?? MOCK_NAIA_PRODUCTION).productionData;
  // const rows: NAIAProductionRow[] = data || [];
  const rows: NAIAProductionRow[] = data || [];

  return (
    <NamedBox
      className={clsx(classes.root, className)}
      headerNameClassName={classes.headerName}
      name="NAIA Stats"
    >
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th
                className={clsx(
                  classes.theadCell,
                  classes.stickyHeaderTop,
                  classes.stickyCol,
                  classes.thFirstCol,
                )}
                rowSpan={2}
              >
                Year
              </th>
              <th
                className={clsx(
                  classes.theadCell,
                  classes.stickyHeaderTop,
                  classes.stickyCol,
                  classes.thSecondCol,
                )}
                rowSpan={2}
              >
                Team
              </th>
              <th
                className={clsx(classes.theadCell, classes.stickyHeaderTop)}
                rowSpan={2}
              >
                G
              </th>
              <th
                className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                colSpan={5}
              >
                Passing
              </th>
              <th
                className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                colSpan={3}
              >
                Rushing
              </th>
              <th
                className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                colSpan={3}
              >
                Receiving
              </th>
              <th
                className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                colSpan={3}
              >
                Defense
              </th>
            </tr>
            <tr className={classes.groupHeaderRow}>
              {/* Passing */}
              <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Comp</th>
              <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Att</th>
              <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Yds</th>
              <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>TD</th>
              <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>INT</th>
              {/* Rushing */}
              <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Att</th>
              <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Yds</th>
              <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>TD</th>
              {/* Receiving */}
              <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Rec</th>
              <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Yds</th>
              <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>TD</th>
              {/* Defense */}
              <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Tckls</th>
              <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>Sacks</th>
              <th className={clsx(classes.theadCell, classes.stickyHeaderSecond)}>INTs</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.year}-${row.teamName}`} className={classes.bodyRow}>
                <td className={clsx(classes.bodyCell, classes.bodyCellFirst, classes.yearCell)}>
                  {row.year}
                </td>
                <td className={clsx(classes.bodyCell, classes.bodyCellSecond)}>
                  {row.teamName}
                </td>
                <td className={classes.bodyCell}>{row.gamesPlayed}</td>
                {/* Passing */}
                <td className={classes.bodyCell}>{row.passingComp}</td>
                <td className={classes.bodyCell}>{row.passingAtt}</td>
                <td className={classes.bodyCell}>{row.passingYds}</td>
                <td className={classes.bodyCell}>{row.passingTD}</td>
                <td className={classes.bodyCell}>{row.passingInt}</td>
                {/* Rushing */}
                <td className={classes.bodyCell}>{row.rushingAtt}</td>
                <td className={classes.bodyCell}>{row.rushingYds}</td>
                <td className={classes.bodyCell}>{row.rushingTD}</td>
                {/* Receiving */}
                <td className={classes.bodyCell}>{row.receiving}</td>
                <td className={classes.bodyCell}>{row.receivingYds}</td>
                <td className={classes.bodyCell}>{row.receivingTD}</td>
                {/* Defense */}
                <td className={classes.bodyCell}>{row.tackles}</td>
                <td className={classes.bodyCell}>{row.sacks}</td>
                <td className={classes.bodyCell}>{row.interceptions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </NamedBox>
  );
}

